import { Injectable } from '@angular/core';
import { ExclusionGroupConfig } from '@portal/app/shared/types';
import { BehaviorSubject } from 'rxjs';

export interface ExclusionGroupState {
  filterLiteralId: string;
  selectedSegments: string[];
  config: ExclusionGroupConfig;
}

@Injectable({ providedIn: 'root' })
export class HaloSegmentService {
  readonly exclusionGroupState$ =
    new BehaviorSubject<ExclusionGroupState | null>(null);

  /** Suppresses the Halo enable popup until the user leaves the dashboard or refreshes. */
  dontShowHaloModal = false;

  resetHaloModalSuppression(): void {
    this.dontShowHaloModal = false;
  }

  hasExcludedSegmentSelected(
    selectedSegments: string[],
    keyword: string
  ): boolean {
    return selectedSegments.some((s) => s.includes(keyword));
  }

  /**
   * Apply mutual exclusivity on segment change.
   * If a keyword segment was added, keep only keyword segments and vice versa.
   * Returns the resolved list, or the original if no change detected.
   */
  resolveExclusionGroup(
    currentValue: string[],
    previousValue: string[],
    allOptions: string[],
    config: ExclusionGroupConfig
  ): string[] {
    if (!config.mutuallyExclusive) {
      return currentValue;
    }
    const added = currentValue.filter((s) => !previousValue.includes(s));
    if (added.length === 0) {
      return currentValue;
    }
    const changedSegment = added[0] as string;
    const isExcluded = changedSegment.includes(config.keyword);
    const result = currentValue.filter((s) =>
      isExcluded ? s.includes(config.keyword) : !s.includes(config.keyword)
    );
    if (result.length === 0 && config.excludeByDefault) {
      return allOptions.filter((s) => !s.includes(config.keyword));
    }
    return result.length > 0 ? result : currentValue;
  }

  notifySelectionChange(
    filterLiteralId: string,
    selectedSegments: string[],
    config: ExclusionGroupConfig
  ): void {
    this.exclusionGroupState$.next({
      filterLiteralId,
      selectedSegments,
      config
    });
  }
}
